import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import {
  adminRequest,
  clearMfaToken,
  readMfaToken,
  saveMfaToken,
  type AdminStatus
} from "./adminApi";
import { supabase } from "./supabase";
import { Layout } from "./components/Layout";
import { LoadingPanel } from "./components/LoadingPanel";
import { DashboardPage } from "./pages/DashboardPage";
import { InvitationsPage } from "./pages/InvitationsPage";
import { RunsPage } from "./pages/RunsPage";
import { UsersPage } from "./pages/UsersPage";
import { UserDetailPage } from "./pages/UserDetailPage";
import { LogsPage } from "./pages/LogsPage";
import { WorkflowsPage } from "./pages/WorkflowsPage";
import "./styles.css";

type Notice = { tone: "error" | "success"; text: string } | null;

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [status, setStatus] = useState<AdminStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(() => readMfaToken());
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (!nextSession) {
        clearMfaToken();
        setMfaToken(null);
        setStatus(null);
      }
      setAuthReady(true);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) {
      setCheckingStatus(false);
      return;
    }
    let active = true;
    setCheckingStatus(true);
    void adminRequest<AdminStatus>("/status", { mfaToken, session })
      .then((nextStatus) => {
        if (active) setStatus(nextStatus);
      })
      .catch((caught) => {
        if (!active) return;
        clearMfaToken();
        setMfaToken(null);
        setStatus(null);
        setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Admin access check failed." });
      })
      .finally(() => {
        if (active) setCheckingStatus(false);
      });

    return () => {
      active = false;
    };
  }, [mfaToken, session]);

  if (!authReady) return <AuthFrame><LoadingPanel label="Checking admin session…" /></AuthFrame>;
  if (!session) return <AuthFrame><LoginPanel /></AuthFrame>;
  if (checkingStatus || !status) {
    return (
      <AuthFrame notice={notice} onSignOut={() => void signOut()}>
        <LoadingPanel label="Checking admin access…" />
      </AuthFrame>
    );
  }
  if (!status.admin) {
    return (
      <AuthFrame notice={notice} onSignOut={() => void signOut()}>
        <AccessDenied onSignOut={() => void signOut()} />
      </AuthFrame>
    );
  }
  if (status.admin.mfa_required && !status.mfa.verified) {
    return (
      <AuthFrame notice={notice} onSignOut={() => void signOut()}>
        <MfaPanel
          session={session}
          onError={(text) => setNotice({ tone: "error", text })}
          onVerified={(token) => {
            saveMfaToken(token);
            setMfaToken(token);
            setNotice({ tone: "success", text: "Admin email MFA verified." });
          }}
        />
      </AuthFrame>
    );
  }

  return (
    <BrowserRouter>
      <Layout adminEmail={status.admin.email} notice={notice} onSignOut={() => void signOut()}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage mfaToken={mfaToken} session={session} setNotice={setNotice} />} />
          <Route path="/invitations" element={<InvitationsPage mfaToken={mfaToken} session={session} setNotice={setNotice} />} />
          <Route path="/runs" element={<RunsPage mfaToken={mfaToken} session={session} setNotice={setNotice} />} />
          <Route path="/users" element={<UsersPage mfaToken={mfaToken} session={session} setNotice={setNotice} />} />
          <Route path="/users/:userId" element={<UserDetailPage mfaToken={mfaToken} session={session} setNotice={setNotice} />} />
          <Route path="/workflows" element={<WorkflowsPage mfaToken={mfaToken} session={session} setNotice={setNotice} />} />
          <Route path="/logs" element={<LogsPage mfaToken={mfaToken} session={session} setNotice={setNotice} />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

function AuthFrame({
  children,
  notice,
  onSignOut
}: {
  children: React.ReactNode;
  notice?: Notice;
  onSignOut?: () => void;
}) {
  return (
    <main className="authFrame">
      {onSignOut ? (
        <div className="authSignOut">
          <button onClick={onSignOut} type="button">Sign out</button>
        </div>
      ) : null}
      <div className="authCard">
        <img alt="Tesflows" className="authLogo" src="/logo.png" />
        <h2 style={{ textAlign: "center", fontWeight: 700, letterSpacing: "-0.02em", marginTop: -8 }}>
          <span style={{ color: "#F4F4F6" }}>Tes</span><span style={{ color: "#E31937" }}>flows</span>
        </h2>
        {notice ? <div className={`notice ${notice.tone}`}>{notice.text}</div> : null}
        {children}
      </div>
    </main>
  );
}

function LoginPanel() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      });
      if (signInError) throw signInError;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Sign in</h1>
      <p>Sign in with your Tesflows admin account. Email MFA is required after password verification.</p>
      <form className="form" onSubmit={(event) => void submit(event)}>
        <label>
          Email
          <input autoComplete="email" inputMode="email" onChange={(event) => setEmail(event.target.value)} required type="email" value={email} />
        </label>
        <label>
          Password
          <input autoComplete="current-password" minLength={8} onChange={(event) => setPassword(event.target.value)} required type="password" value={password} />
        </label>
        {error ? <div className="inlineError">{error}</div> : null}
        <button disabled={busy} type="submit">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </>
  );
}

function MfaPanel({
  onError,
  onVerified,
  session
}: {
  onError(text: string): void;
  onVerified(token: string): void;
  session: Session;
}) {
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);

  async function start() {
    setBusy(true);
    try {
      await adminRequest<{ expires_at: string }>("/mfa/start", { method: "POST", session });
      setSent(true);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not send MFA code.");
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await adminRequest<{ expires_at: string; token: string }>("/mfa/verify", {
        body: { code },
        method: "POST",
        session
      });
      onVerified(result.token);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not verify MFA code.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Verify access</h1>
      <p>A one-time code was sent to your admin email. Enter it below to continue.</p>
      <div className="actions">
        <button disabled={busy} onClick={() => void start()} type="button">{sent ? "Resend code" : "Send code"}</button>
      </div>
      <form className="form" onSubmit={(event) => void verify(event)}>
        <label>
          Verification code
          <input inputMode="numeric" maxLength={6} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} pattern="[0-9]{6}" required value={code} />
        </label>
        <button disabled={busy || code.length !== 6} type="submit">{busy ? "Verifying…" : "Verify"}</button>
      </form>
    </>
  );
}

function AccessDenied({ onSignOut }: { onSignOut(): void }) {
  return (
    <>
      <h1>Access denied</h1>
      <p>This account does not have admin access. Sign in with an authorized admin account.</p>
      <button onClick={onSignOut} type="button">Sign out</button>
    </>
  );
}

async function signOut() {
  clearMfaToken();
  await supabase.auth.signOut();
}

createRoot(document.getElementById("root")!).render(<App />);
